import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, Fieldset, Heading, Layout, RadioButton } from '@vwfs-bronson/bronson-react';
import { appendMissingDecimalPart } from '../../../services/common/formatNew';
import { SetState } from '../../../services/common/miscUtils.types';

const tollBadgeNames = ['Toll Badge Services Euro', 'Toll Badge Services France'];
const regionNames = ['Total Fuel Card Euro', 'Total Fuel Card France'];
const notSelectedId = '100350';

export const isFuelTypeProduct = (name) => {
  return regionNames.includes(name) || tollBadgeNames.includes(name);
};

export const isNonTollBadgeFuelProduct = (name) => {
  return regionNames.includes(name);
};

const regions = [
  { name: 'Euro', label: 'EuroTraffic' },
  { name: 'France', label: 'France' },
] as const;

interface Props {
  isFormDisabled: boolean;
  setSelectedAllAdditionalProducts: SetState<QuoteSchema.Schemas.AdditionalProductsGetQuoteItem[]>;
  selectedAdditionalProducts;
  fuelDepositAmount: number;
  managementFees: number;
  resetFuelPrices: () => void;
}

const fuelOptionsTranslations = {
  '401837': 'noService',
  '401838': 'wash',
  '401839': 'lubricating',
  '401840': 'washLubric',
  '401841': 'washingLubrcantShopMaintenance',
};

const tollBadgeFuelOption = { id: '401842', translationKey: 'tollBadge' };

export const FuelService: FunctionComponent<Props> = ({
  setSelectedAllAdditionalProducts,
  selectedAdditionalProducts,
  fuelDepositAmount,
  managementFees,
  isFormDisabled,
  resetFuelPrices,
}) => {
  const { t } = useTranslation();
  const [selectedFuelRegion, setSelectedFuelRegion] = useState<'Euro' | 'France' | null>(null);
  const [fuelCardSectionVisible, setFuelCardSectionVisible] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState(selectedAdditionalProducts);
  const isInternalUpdate = useRef(false);

  useEffect(() => {
    if (!isInternalUpdate.current) {
      setSelectedProducts(selectedAdditionalProducts);
    }
    isInternalUpdate.current = false;
  }, [selectedAdditionalProducts]);

  useEffect(() => {
    const isAnyProductSelected = selectedProducts
      .filter((product) => isFuelTypeProduct(product.name))
      .some((product) => product.choices.some((choice) => choice.isSelected && choice.id !== notSelectedId));

    const isAnyEuroSelected = selectedProducts
      .find((product) => product.name === 'Total Fuel Card Euro')
      ?.choices.some((choice) => choice.isSelected && choice.id !== notSelectedId);

    const isAnyFranceSelected = selectedProducts
      .find((product) => product.name === 'Total Fuel Card France')
      ?.choices.some((choice) => choice.isSelected && choice.id !== notSelectedId);

    if (isAnyEuroSelected) {
      setSelectedFuelRegion('Euro');
    }

    if (isAnyFranceSelected) {
      setSelectedFuelRegion('France');
    }

    setFuelCardSectionVisible(isAnyProductSelected);
  }, [selectedProducts]);

  useEffect(() => {
    if (fuelCardSectionVisible) {
      return;
    }

    resetFuelPrices();
    setSelectedFuelRegion(null);

    isInternalUpdate.current = true;
    setSelectedProducts((products) =>
      products.map((product) => {
        if (!isFuelTypeProduct(product.name)) {
          return product;
        }

        return {
          ...product,
          choices: product.choices.map((choice) => ({
            ...choice,
            isSelected: choice.id === notSelectedId,
          })),
        };
      })
    );
  }, [fuelCardSectionVisible, resetFuelPrices]);

  useEffect(() => {
    if (selectedProducts !== selectedAdditionalProducts) {
      isInternalUpdate.current = true;
      setSelectedAllAdditionalProducts(selectedProducts);
    }
  }, [selectedProducts, selectedAdditionalProducts, setSelectedAllAdditionalProducts]);

  const onFuelCardSectionVisibleChange = () => {
    setFuelCardSectionVisible((visible) => !visible);
  };

  const selectFuelOption = (chosen: string) => {
    isInternalUpdate.current = true;

    setSelectedProducts((products) =>
      products.map((product) => {
        if (!regionNames.includes(product.name)) {
          return product;
        }
        return {
          ...product,
          choices: product.choices.map((choice) => {
            return product.name === `Total Fuel Card ${selectedFuelRegion}`
              ? {
                  ...choice,
                  isSelected: choice.id === chosen,
                }
              : { ...choice, isSelected: choice.id === notSelectedId };
          }),
        };
      })
    );
  };

  const selectTollBadge = () => {
    isInternalUpdate.current = true;

    setSelectedProducts((products) =>
      products.map((product) => {
        if (!tollBadgeNames.includes(product.name)) {
          return product;
        }
        return {
          ...product,
          choices: product.choices.map((choice) => {
            return product.name === `Toll Badge Services ${selectedFuelRegion}`
              ? {
                  ...choice,
                  isSelected: !choice.isSelected,
                }
              : { ...choice, isSelected: choice.id === notSelectedId };
          }),
        };
      })
    );
  };

  return (
    <Fieldset.Row>
      <Layout>
        <Layout.Item className="u-mt">
          <Heading className="u-m-none" level={5}>
            {t('step3:fuelCard:title')}
          </Heading>
        </Layout.Item>

        <Layout.Item default="1/1">
          <Checkbox
            onChange={onFuelCardSectionVisibleChange}
            checked={fuelCardSectionVisible}
            disabled={isFormDisabled}
          >
            {t('step3:fuelCard:title')}
          </Checkbox>
        </Layout.Item>
        {fuelCardSectionVisible && (
          <>
            <Layout.Item className="u-ml-small">
              {regions.map((region) => (
                <RadioButton
                  key={region.name}
                  className="u-mr"
                  disabled={isFormDisabled}
                  name={region.name}
                  checked={region.name === selectedFuelRegion}
                  onChange={() => setSelectedFuelRegion(region.name)}
                >
                  {region.label}
                </RadioButton>
              ))}
            </Layout.Item>
            {selectedFuelRegion && (
              <>
                <Layout.Item className="u-ml-small">
                  <Heading level={6}>{t('step3:fuelCard:fuelCardOptionsTitle')}</Heading>
                  {selectedProducts
                    .find((options) => options.name === `Total Fuel Card ${selectedFuelRegion}`)
                    ?.choices.filter((choice) => choice.id !== '100350')
                    .map((choice) => (
                      <Layout.Item key={choice.id} className="u-mt-xsmall">
                        <RadioButton
                          onChange={() => selectFuelOption(choice.id)}
                          checked={choice.isSelected}
                          disabled={isFormDisabled}
                          name={choice.name}
                        >
                          {t(`step3:fuelCard:fuelCardOptions:${fuelOptionsTranslations[choice.id]}`)}
                        </RadioButton>
                      </Layout.Item>
                    ))}
                </Layout.Item>
                <Layout.Item className="u-ml-small">
                  {/* eslint-disable-next-line @typescript-eslint/no-empty-function */}
                  <Checkbox
                    onChange={selectTollBadge}
                    disabled={isFormDisabled}
                    checked={
                      selectedProducts
                        .find((product) => product.name === `Toll Badge Services ${selectedFuelRegion}`)
                        ?.choices.find((choice) => choice.id === tollBadgeFuelOption.id).isSelected
                    }
                  >
                    {t(`step3:fuelCard:fuelCardOptions:${tollBadgeFuelOption.translationKey}`)}
                  </Checkbox>
                </Layout.Item>
                <Layout.Item>
                  <strong className="u-mr-small">{t('step3:fuelCard:managementFees')}</strong>
                  {appendMissingDecimalPart(fuelDepositAmount, 2)}
                </Layout.Item>
                <Layout.Item>
                  <>
                    <strong className="u-mr-small">{t('step3:fuelCard:fuelDeposit')}</strong>
                    {appendMissingDecimalPart(managementFees, 2)}
                  </>
                </Layout.Item>
              </>
            )}
          </>
        )}
      </Layout>
    </Fieldset.Row>
  );
};
